import { inject, observer } from 'mobx-react';
import React, { useCallback, useEffect, useState } from 'react';
import DefaultButtonEx from '@v2/components/base/DefaultButtonEx';
import SearchableComboBox from 'src/components/base/SearchableComboBox';
import { mergeStyleSets, Checkbox } from '@fluentui/react';
import { runShortAction } from 'src/services/utils';
import useUpdateEffect from 'react-use/lib/useUpdateEffect';
import { FieldSelector } from 'src/components/Flowsteps/PushData/Mapping/common';
import { pick } from 'shared/utils/utils';
import CellAddressPicker from '@v2/components/base/CellAddressPicker';
import { FieldTypeName as Ftn } from 'shared/utils/queryAnalysis';
import office from 'src/services/office';

const classNames = mergeStyleSets({
  container: {
    display: 'flex',
    flexDirection: 'column',
    padding: '0 10px'
  },
  button: {
    width: 'max-content'
  },
  form: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'start',
    gap: '10px',
    width: '100%',
  },
  buttons: {
    display: 'flex',
    justifyContnet: 'center',
    gap: '5px',
    width: '100%',
  }
});

function AssingLookup({ officeStore, onSave }) {

  const [showForm, setShowForm] = useState(false);
  const [showLabels, setShowLabels] = useState(false);
  const [objects, setObjects] = useState([]);
  const [selectedObjectName, setSelectedObjectName] = useState(null);
  const [objectFields, setObjectsFields] = useState([]);
  const [objectOptions, setObjectOptions] = useState([]);
  const [selectedFieldName, setSelectedFieldName] = useState(null);
  const [idCellAddress, setIdCellAddress] = useState(null);

  //id column
  const [columnAddress, setColumnAddress] = useState(null);
  const [idColumns, setIdColumns] = useState([]);
  const [idColumnId, setIdColumnId] = useState(null);

  const updateObjects = () => {
    const selector = showLabels ? i => i.label : i => i.name;
    setObjects(officeStore.objects.map(i => ({ key: i.name, text: selector(i) })));
  }

  useEffect(() => {
    if (!showForm || objects.length) {
      return;
    }
    runShortAction(async () => {
      await officeStore.contextStore.updateNamedRanges();
      await officeStore.fetchObjects();
      updateObjects();

      //check 'table mode'
      await Excel.run(async (ctx) => {
        const selectedRange = ctx.workbook.getSelectedRange();
        const { table, columns } = await office.getSelectedTableColumns(selectedRange);
        if (!table || !columns) {
          return;
        }

        const columnsProps = columns.map(column => ({
          option: { key: column.id, text: column.name },
          intersection: selectedRange.getIntersectionOrNullObject(column.getRange())
        }));
        await ctx.sync();

        let lookupColumn;
        const columnsOptions = [];

        columnsProps.forEach(({ option, intersection }) => {
          if (!intersection.isNullObject && !lookupColumn) {
            lookupColumn = { tableId: table.id, columnId: option.key }
          }
          else {
            columnsOptions.push(option);
          }
        });

        setColumnAddress(lookupColumn);
        setIdColumns(columnsOptions);
      });
    });
  }, [showForm, objects]);

  useUpdateEffect(() => {
    updateObjects();
    updateObjectOptions();
  }, [showLabels]);

  useEffect(() => {
    updateObjectOptions();
    setSelectedFieldName(undefined);
  }, [objectFields]);

  function updateObjectOptions() {
    const selector = showLabels ? (i) => i.label : (i) => i.name;
    setObjectOptions(objectFields.reduce((out, fieldData) => {
      const { field } = fieldData;
      const { readOnly, type, name: id } = field;
      if ([Ftn.Id, Ftn.String, Ftn.Phone, Ftn.Email].includes(type)) {
        out.push({ key: id, id, text: selector(field), type, readOnly, fieldData });
      }
      return out;
    }, []));
  }


  function handleObjectChange(_, { key: objectName }) {
    setSelectedObjectName(objectName);
    runShortAction(async () => {
      const rawFields = await officeStore.fetchObjectFields(objectName);
      const fields = rawFields.map(rawField => {
        const field = { name: rawField.name, rawField, field: rawField, objectName };
        return {
          ...field,
          field: { readOnly: !field.rawField.updateable, ...pick(field.field, ['label', 'name', 'type', 'idLookup', 'upsertRelationship']) },
        }
      });
      setObjectsFields(fields);
    });
  }

  function handleFieldChange(item) {
    setSelectedFieldName(item.key);
  }

  function getActiveWorksheetId() {
    return officeStore.activeWorksheetId;
  }

  function handleCancel() {
    setShowForm(false);
    setSelectedObjectName(undefined);
    setSelectedFieldName(undefined);
  }

  function handleSave() {
    const toSave = Object.assign(
      {
        objectName: selectedObjectName,
        fieldName: selectedFieldName
      },
      idCellAddress && { idCellAddress },
      columnAddress && { ...columnAddress },
      idColumnId && { idColumnId }
    );
    onSave(toSave);
  }

  return (
    <div className={classNames.container}>
      {showForm
        ?
        <div className={classNames.form}>
          <Checkbox label="Show labels"
            value={showLabels}
            onChange={(_, checked) => setShowLabels(checked)}
          />
          <SearchableComboBox
            options={objects}
            selectedKey={selectedObjectName}
            onChange={handleObjectChange}
            placeholder="Select object"
          />
          <FieldSelector
            disabled={!selectedObjectName}
            options={objectOptions}
            onChange={handleFieldChange}
            selectedKey={selectedFieldName}
            placeholder="Select field"
          />
          <div>
            {
              columnAddress
                ?
                <SearchableComboBox
                  options={idColumns}
                  selectedKey={idColumnId}
                  onChange={(event, item) => setIdColumnId(item.key)}
                  placeholder="Select id column"
                />
                :
                <CellAddressPicker
                  value={idCellAddress}
                  onChange={address => setIdCellAddress(address)}
                  allowEmptyCell
                  allowTargetPicker={false}
                  label='Put record id into cell (optional)'
                  localAddressSheetIdGetter={getActiveWorksheetId}
                />
            }
          </div>
          <div className={classNames.buttons}>
            <DefaultButtonEx
              text="Cancel"
              onClick={handleCancel}
            />
            <DefaultButtonEx
              text="Save"
              primary
              disabled={!selectedFieldName}
              onClick={handleSave}
            />
          </div>
        </div>
        :
        <DefaultButtonEx
          className={classNames.button}
          text='Assign Lookup'
          primary
          onClick={() => setShowForm(true)}
        />
      }

    </div>
  )
}

export default inject('officeStore')(observer(AssingLookup))